import {
  AutoComplete as UIFAutoComplete,
  ReadOnly,
  SnackbarApiErrorMessage,
  useDataProvider,
  useEntityPermission,
  useSnackbar,
  useLocalization,
} from '@ui-framework/index';
import { Box, Tooltip, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import IconLibrary from '../IconLibrary';

const RESULT_LIMIT = 10;
const emptyLocationResult = { data: [], total: 0 };
const LOCATION_INDICATOR_TOOLTIP_PROPS = {
  arrow: true,
  placement: 'top',
  slotProps: {
    popper: {
      sx: {
        zIndex: 10000,
      },
    },
  },
};

const SKILL_TYPE_LABELS = {
  BRAND: 'Brand',
  PRODUCT_LINE: 'Product Line',
  INDUSTRY: 'Industry',
};

function formatList(values) {
  if (values.length <= 1) return values[0] || '';
  if (values.length === 2) return values.join(' and ');
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
}

function formatDistanceFromCustomerMiles(distanceFromCustomerMiles) {
  const distance = Number(distanceFromCustomerMiles);

  if (!Number.isFinite(distance)) return '';

  return `${Number(distance.toFixed(1))} mi`;
}

function buildSkillMismatchTooltip(option, formatMessage) {
  const missingSkills =
    option?.skillMismatch?.map(skill => {
      const label = SKILL_TYPE_LABELS[skill.type] || skill.type;
      return `${label} (${skill.value})`;
    }) || [];

  if (!missingSkills.length) return 'Skills mismatch';

  return `${formatMessage({
    id: 'WARNING_MAY_NOT_BE_A_GOOD_FIT_FOR_THIS_JOB',
    defaultMessage: 'Warning: May not be a good fit for this job.',
  })} ${formatMessage({
    id: 'MISSING',
    defaultMessage: 'Missing:',
  })} ${formatList(missingSkills)}`;
}

function hasSkillMismatch(option) {
  return Boolean(option?.skillMismatch?.length);
}

export function toLocationOption(location) {
  if (!location) return null;

  return {
    ...location,
    distanceFromCustomerMiles: location.distanceFromCustomerMiles,
    label: location.name || location.locationName || location.label || '',
    value: location.id ?? location.value,
  };
}

export async function getDispatchEventLocation({
  serviceJobId,
  dispatchId,
  dispatchEventId,
  apiUrl,
  get,
  snackbar,
}) {
  const missingDispatchEventIds =
    !serviceJobId || !dispatchId || !dispatchEventId;
  if (missingDispatchEventIds) {
    return null;
  }
  const url = `${apiUrl}/dispatch-advisor/v1/service-jobs/${encodeURIComponent(
    serviceJobId,
  )}/dispatches/${encodeURIComponent(
    dispatchId,
  )}/events/${encodeURIComponent(dispatchEventId)}/dispatch-event-location-recommendation`;
  const result = await get(url);
  if (result.ok) {
    return result?.data;
  }
  snackbar.showMessage({
    type: 'error',
    data: <SnackbarApiErrorMessage errors={result?.errors} />,
  });
  return null;
}

function LocationIndicators({ option }) {
  const { formatMessage } = useLocalization();
  if (!option) return null;

  return (
    <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, ml: 1 }}>
      {option.rework ? (
        <Tooltip
          {...LOCATION_INDICATOR_TOOLTIP_PROPS}
          title={formatMessage({
            id: 'REWORK_LOCATION_TOOLTIP',
            defaultMessage: 'Original servicing location for this job',
          })}>
          <IconLibrary.ReworkLocation color="warning" fontSize="small" />
        </Tooltip>
      ) : null}
      {hasSkillMismatch(option) ? (
        <Tooltip
          {...LOCATION_INDICATOR_TOOLTIP_PROPS}
          title={buildSkillMismatchTooltip(option, formatMessage)}>
          <IconLibrary.SkillMismatch color="warning" fontSize="small" />
        </Tooltip>
      ) : null}
      {option.outsideServiceArea ? (
        <Tooltip
          {...LOCATION_INDICATOR_TOOLTIP_PROPS}
          title={formatMessage({
            id: 'OUTSIDE_SERVICE_AREA_TOOLTIP',
            defaultMessage:
              'This location does not normally service this address but may accept the job due to proximity.',
          })}>
          <IconLibrary.OutsideServiceArea color="error" fontSize="small" />
        </Tooltip>
      ) : null}
    </Box>
  );
}

function LocationOptionLabel({ option }) {
  const distanceLabel = formatDistanceFromCustomerMiles(
    option?.distanceFromCustomerMiles,
  );

  return (
    <Box
      component="span"
      sx={{ alignItems: 'center', display: 'inline-flex', minWidth: 0 }}>
      <Typography
        component="span"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {option?.label || ''}
      </Typography>
      {distanceLabel ? (
        <Typography
          color="text.secondary"
          component="span"
          sx={{ flexShrink: 0, ml: 1 }}>
          {distanceLabel}
        </Typography>
      ) : null}
      <LocationIndicators option={option} />
    </Box>
  );
}

const formatLocationOptionLabel = option => (
  <LocationOptionLabel option={option} />
);

const getLocationOptionValue = option => option.value;

const ServiceProviderRecommendedLocations = forwardRef(
  (
    {
      field,
      permission,
      helperText: helperTextProp,
      onLocationsAvailabilityChange,
      overwriteProps = {},
      ...props
    },
    ref,
  ) => {
    const [defaultOptions, setDefaultOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const { control, getValues, watch } = useFormContext();
    const snackbar = useSnackbar();
    const { get, getApiUrl } = useDataProvider('dispatchAdvisor');
    const { isHidden, isReadOnly } = useEntityPermission();
    const { formatMessage } = useLocalization();

    const fieldName = field?.name;
    const serviceJobIdField = field?.serviceJobIdField || 'serviceJobId';
    const serviceProviderIdField =
      field?.serviceProviderIdField || 'serviceProviderId';
    const serviceJobId = field?.serviceJobId || watch(serviceJobIdField);
    const serviceProviderId = watch(serviceProviderIdField);
    const currentValue = watch(fieldName);
    const currentLabel = getValues(fieldName)?.label;

    const missingRequiredIds = !serviceJobId || !serviceProviderId;

    const currentValueRef = useRef(currentValue);

    const buildUrl = useCallback(
      ({ searchValue = '' } = {}) => {
        const params = new URLSearchParams({
          page: '1',
          limit: `${RESULT_LIMIT}`,
        });

        if (searchValue) {
          params.set('locationName', searchValue);
        }

        return `${getApiUrl()}/dispatch-advisor/v1/service-jobs/${serviceJobId}/service-providers/${serviceProviderId}/locations?${params.toString()}`;
      },
      [getApiUrl, serviceJobId, serviceProviderId],
    );

    const showApiError = useCallback(
      result => {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={result?.errors} />,
        });
      },
      [snackbar],
    );

    const getLocations = useCallback(
      async ({ searchValue = '' } = {}) => {
        if (missingRequiredIds) {
          return emptyLocationResult;
        }

        const url = buildUrl({ searchValue });

        const result = await get(url);

        if (result.ok) {
          return {
            data: result?.data?.data?.map(toLocationOption) || [],
          };
        }

        showApiError(result);
        return emptyLocationResult;
      },
      [buildUrl, get, missingRequiredIds, showApiError],
    );

    const loadOptions = async (searchValue, callback) => {
      if (!searchValue || searchValue.length < 2) {
        callback(defaultOptions);
        return;
      }

      const result = await getLocations({ searchValue });
      callback(result.data);
    };

    const helperText = useMemo(() => {
      if (missingRequiredIds) {
        return formatMessage({
          id: 'SELECT_SERVICE_JOB_AND_SERVICE_PROVIDER_BEFORE_CHOOSING_LOCATION',
          defaultMessage:
            'Select a service job and service provider before choosing a location.',
        });
      }

      return helperTextProp;
    }, [formatMessage, helperTextProp, missingRequiredIds]);

    const noLocationsAvailable =
      !isLoading && !missingRequiredIds && defaultOptions.length === 0;

    const noLocationsMessage = formatMessage({
      id: 'ACTIVE_LOCATION_REQUIRED_TO_ACCEPT_JOB',
      defaultMessage:
        'At least one active location is required to accept the job. Please update your profile to add or activate a location.',
    });

    const initialize = async () => {
      setIsLoading(true);
      try {
        const locations = await getLocations();
        setDefaultOptions(locations.data);
        currentValueRef.current = currentValue;
      } catch (e) {
        setDefaultOptions([]);
      }
      setIsLoading(false);
    };

    useEffect(() => {
      initialize();
    }, []);

    useEffect(() => {
      if (!onLocationsAvailabilityChange || isLoading) return;
      onLocationsAvailabilityChange(!noLocationsAvailable);
    }, [isLoading, noLocationsAvailable, onLocationsAvailabilityChange]);

    if (isReadOnly(permission, currentLabel)) {
      return <ReadOnly name={fieldName}>{currentLabel}</ReadOnly>;
    }

    if (isHidden(permission)) {
      return (
        <input
          type="hidden"
          name={fieldName}
          value={currentValue?.value || ''}
        />
      );
    }

    if (noLocationsAvailable) {
      return (
        <Typography color="warning.main" variant="body2">
          {noLocationsMessage}
        </Typography>
      );
    }

    return (
      <UIFAutoComplete
        {...props}
        control={control}
        name={fieldName}
        ref={ref}
        defaultOptions={defaultOptions}
        loadOptions={loadOptions}
        formatOptionLabel={formatLocationOptionLabel}
        getOptionValue={getLocationOptionValue}
        isClearable
        isDisabled={missingRequiredIds || isLoading}
        helperText={helperText}
        overwriteProps={{
          noOptionsMessage: () =>
            missingRequiredIds
              ? formatMessage({
                  id: 'SERVICE_JOB_AND_SERVICE_PROVIDER_REQUIRED',
                  defaultMessage: 'Service job and service provider required',
                })
              : formatMessage({
                  id: 'NO_LOCATIONS_FOUND',
                  defaultMessage: 'No locations found',
                }),
          ...overwriteProps,
        }}
      />
    );
  },
);

export default observer(ServiceProviderRecommendedLocations);
